import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { HttpService, NotificationService } from '../../../tms-shared';
import { TmsWebBaseEditService } from '../base';
import {
  PickingListLabel,
  ProductStockAllocations,
  PurchaseOrderLineStockAllocation,
  PurchaseOrderLineStockIssue,
  SalesOrderStockAllocation,
  SalesOrPurchaseOrderStockAllocation,
  StockItem,
  StockItemAllocation,
  StockItemAllocations,
  StockItemCosts,
  StockItemDetails,
  StockItemLabel,
  StockItemOrderHistory,
  StockItemSplit,
} from '../../models';

@Injectable({
  providedIn: 'root',
})
export class StockItemService extends TmsWebBaseEditService<StockItem, StockItemDetails> {
  public refreshDetails = new Subject<void>();

  constructor(
    protected httpService: HttpService,
    protected notificationService: NotificationService,
  ) {
    super('StockItem', $localize`Stock Item`, httpService, notificationService);
  }

  salesOrderStockAllocation(id: string): Observable<SalesOrderStockAllocation[]> {
    return this.httpService.get<SalesOrderStockAllocation[]>(`${this.apiUrl}/SalesOrderStockAllocation?id=${id}`);
  }

  salesOrPurchaseOrderStockAllocation(id: string): Observable<SalesOrPurchaseOrderStockAllocation[]> {
    return this.httpService.get<SalesOrPurchaseOrderStockAllocation[]>(
      `${this.apiUrl}/SalesOrPurchaseOrderStockAllocation?id=${id}`,
    );
  }

  stockItemAllocations(id: string): Observable<StockItemAllocations> {
    return this.httpService.get<StockItemAllocations>(`${this.apiUrl}/StockItemAllocation?id=${id}`);
  }

  productStockAllocations(id: string): Observable<ProductStockAllocations> {
    return this.httpService.get<ProductStockAllocations>(`${this.apiUrl}/ProductStockAllocation?id=${id}`);
  }

  stockItemsByProduct(id: string): Observable<StockItem[]> {
    return this.httpService.get<StockItem[]>(`${this.apiUrl}/StockItemsByProduct?id=${id}`);
  }

  allocationsForStockItem(id: string): Observable<StockItemAllocation[]> {
    return this.httpService.get<StockItemAllocation[]>(`${this.apiUrl}/AllocationsForStockItem?id=${id}`);
  }

  splitsForStockItem(id: string): Observable<StockItemSplit[]> {
    return this.httpService.get<StockItemSplit[]>(`${this.apiUrl}/SplitsForStockItem?id=${id}`);
  }

  stockItemCosts(id: string): Observable<StockItemCosts> {
    return this.httpService.get<StockItemCosts>(`${this.apiUrl}/StockItemCosts?id=${id}`);
  }

  stockItemOrderHistory(id: string): Observable<StockItemOrderHistory[]> {
    return this.httpService.get<StockItemOrderHistory[]>(`${this.apiUrl}/StockItemOrderHistory?id=${id}`);
  }

  callOffStock(id: string): Observable<StockItemDetails[]> {
    return this.httpService.get<StockItemDetails[]>(`${this.apiUrl}/CallOffStock?id=${id}`);
  }

  createForProduct(id: string): Observable<StockItemDetails> {
    return this.httpService.get<StockItemDetails>(`${this.apiUrl}/CreateForProduct?id=${id}`);
  }

  stockItemLabel(id: string): Observable<StockItemLabel> {
    return this.httpService.get<StockItemLabel>(`${this.apiUrl}/StockItemLabel?id=${id}`);
  }

  getStockItemLabel(id: string, prevLocation: boolean): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(
      `${this.apiUrl}/GetStockItemLabel?id=${id}&previousLocation=${prevLocation}`,
    );
  }

  getSplitStockItemLabel(id: string): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(`${this.apiUrl}/GetSplitStockItemLabel?id=${id}`);
  }

  getStockItemLabelByStockNo(stockNo: string): Observable<PickingListLabel> {
    return this.httpService.get<PickingListLabel>(`${this.apiUrl}/GetStockItemLabelByStockNo?stockNo=${stockNo}`);
  }

  getForLabels(ids: string[]): Observable<PickingListLabel[]> {
    return this.httpService.post<PickingListLabel[]>(`${this.apiUrl}/GetForLabels`, { ids });
  }

  exportStockToFTP(): Observable<any> {
    return this.httpService.get<any>(`${this.apiUrl}/FtpCsvStockFile`);
  }

  exportMagentoFtpStockUpdate(webjob: boolean): Observable<any> {
    return this.httpService.get<any>(`${this.apiUrl}/MagentoFtpStockUpdate?webjob=${webjob}`);
  }

  checkStockNo(stockNo: string, id: string): Observable<boolean> {
    return this.httpService.get<boolean>(`${this.apiUrl}/CheckStockNo?stockNo=${stockNo}&id=${id ?? ''}`);
  }

  purchaseOrderStockAllocation(
    id: string,
    purchaseOrderLineRequirementId: string,
    initialDisplay: boolean,
  ): Observable<PurchaseOrderLineStockAllocation[]> {
    return this.httpService.get<PurchaseOrderLineStockAllocation[]>(
      `${this.apiUrl}/PurchaseOrderLineStockAllocation?id=${id}&purchaseOrderLineRequirementId=${purchaseOrderLineRequirementId}&initialDisplay=${initialDisplay}`,
    );
  }

  purchaseOrderStockIssues(id: string): Observable<PurchaseOrderLineStockIssue[]> {
    return this.httpService.get<PurchaseOrderLineStockIssue[]>(`${this.apiUrl}/PurchaseOrderLineStockIssue?id=${id}`);
  }
}
